// Top-level layout: sidebar + chat pane + modals + toast + overflow menu.
// The single … overflow button lives in the topbar's right slot
// (.topbar__actions) and surfaces three items: Stop session (only when a
// per-client claude process is running), Guide, Sign out.
//
// Why Stop session lives in the menu (not as a pill): on phones the
// topbar real-estate is precious; the pill ate breadcrumb room and was
// confusing to first-time users. The action still has real backend work
// (kills the claude child → frees ~500 MB RAM per session) — just
// surfaces less aggressively.

import { useEffect, useRef, useState } from "react";
import { LogOut, MoreHorizontal, HelpCircle, Power, Zap, Settings, Clock, Link, Layers, AlertTriangle } from "react-feather";
import { useReviewSheet } from "../store/reviewSheet";
import { useAuth } from "../auth-context";
import { useSession, THINKING_LABEL } from "../store/session";
import { chatLinkUrl } from "../lib/chatLinks";
import ChatLinkNavigation from "./ChatLinkNavigation";
import CompaniesSidebar from "./CompaniesSidebar";
import ChatPane from "./ChatPane";
import AddCompanyModal from "./AddCompanyModal";
import SessionSettingsModal from "./SessionSettingsModal";
import GlobalSettingsModal from "./GlobalSettingsModal";
import ScheduledTasks from "./ScheduledTasks";
import Guide from "./Guide";
import Lightbox from "./Lightbox";
import RamChip from "./RamChip";
import UpdatePill from "./UpdatePill";
import { copyTextToClipboard } from "../lib/clipboard";
import { runningBuildId } from "../lib/autoUpdate";
import { ExchangeMenuSettings } from "./ExchangeSettings";
import { useIsPhone, usePhoneSidebarSwipe, usePhoneViewport } from "../lib/usePhoneLayout";
import { toastGoesToIsland } from "../lib/islandNotice";

export default function DashboardShell() {
  const { signOut } = useAuth();
  const bootstrap = useSession((s) => s.bootstrap);
  const sidebarOpen = useSession((s) => s.sidebarOpen);
  const setSidebarOpen = useSession((s) => s.setSidebarOpen);
  const companies = useSession((s) => s.companies);
  const activeCompanyId = useSession((s) => s.activeCompanyId);
  const chatNavigationReady = useSession((s) => s.chatNavigationReady);
  const selectCompany = useSession((s) => s.selectCompany);
  const renameCompany = useSession((s) => s.renameCompany);
  const deleteCompany = useSession((s) => s.deleteCompany);
  const convertToClient = useSession((s) => s.convertToClient);
  const clearChatHistory = useSession((s) => s.clearChatHistory);
  const copyCompanyCurl = useSession((s) => s.copyCompanyCurl);
  const visitCompanyWebsite = useSession((s) => s.visitCompanyWebsite);
  const pendingByCompany = useSession((s) => s.pendingByCompany);
  const sessionStatusByCompany = useSession((s) => s.sessionStatusByCompany);
  const openAddCompany = useSession((s) => s.openAddCompany);
  const addSample = useSession((s) => s.addSample);
  const openSettings = useSession((s) => s.openSettings);
  const addCompanyModalOpen = useSession((s) => s.addCompanyModalOpen);
  const settingsCompanyId = useSession((s) => s.settingsCompanyId);
  const guideOpen = useSession((s) => s.guideOpen);
  const lightboxItem = useSession((s) => s.lightboxItem);
  const lightboxItems = useSession((s) => s.lightboxItems);
  const lightboxIndex = useSession((s) => s.lightboxIndex);
  const stepLightbox = useSession((s) => s.stepLightbox);
  const setLightbox = useSession((s) => s.setLightbox);
  const openGuide = useSession((s) => s.openGuide);
  const openGlobalSettings = useSession((s) => s.openGlobalSettings);
  const globalSettingsOpen = useSession((s) => s.globalSettingsOpen);
  const scheduledOpen = useSession((s) => s.scheduledOpen);
  const openScheduled = useSession((s) => s.openScheduled);
  const toast = useSession((s) => s.toast);
  const clearToast = useSession((s) => s.clearToast);
  const sessionStatus = useSession((s) => s.sessionStatus);
  const stopSession = useSession((s) => s.stopSession);
  const toggleAutoApprove = useSession((s) => s.toggleAutoApprove);
  const setAutoRevisionEffort = useSession((s) => s.setAutoRevisionEffort);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);
  const toastRef = useRef<HTMLDivElement>(null);
  // Phones: swipe right opens the chats sidebar, swipe left closes it.
  const isPhone = useIsPhone();
  const islandChat = useSession((s) => s.activeCompanyId);
  usePhoneSidebarSwipe(isPhone && chatNavigationReady, sidebarOpen, setSidebarOpen);
  // Phones: the app lives in the visible area above the keyboard.
  usePhoneViewport(isPhone);

  // Bootstrap store on first mount.
  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // Auto-dismiss toasts after 4s.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(clearToast, toast.action ? 6000 : 4000);
    return () => clearTimeout(t);
  }, [toast, clearToast]);

  // The snackbar (Build's, julian-ui) sits at the bottom just above the input bar, never over it,
  // so it can't steal a tap meant for the composer or sit under a phone keyboard. Measured when it shows.
  useEffect(() => {
    const el = toastRef.current;
    if (!toast || !el) return;
    const bar = document.querySelector(".composer2")?.getBoundingClientRect();
    const viewport = window.visualViewport?.height ?? window.innerHeight;
    if (bar && bar.height > 0 && bar.top < viewport) el.style.setProperty("--toast-bottom", `${Math.max(16, Math.round(window.innerHeight - bar.top + 12))}px`);
    else el.style.removeProperty("--toast-bottom");
  }, [toast]);

  // Click-outside closes the overflow menu.
  useEffect(() => {
    if (!overflowOpen) return;
    function onClick(e: MouseEvent) {
      if (
        overflowRef.current &&
        !overflowRef.current.contains(e.target as Node)
      ) {
        setOverflowOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [overflowOpen]);

  const sessionRunning =
    sessionStatus.state === "running" &&
    sessionStatus.companyId === activeCompanyId;

  // The topbar ⋯ carries a per-client Auto-approve toggle for whichever client is
  // currently open — one click flips it (ON also approves everything pending now).
  const activeCompany = companies.find((c) => c.id === activeCompanyId);
  const autoOn = activeCompany?.autoRevision === true;
  const autoFrozen = activeCompany?.automationFrozen === true;

  return (
    <div
      className={`shell ${sidebarOpen ? "shell--sidebar-open" : ""}`}
      data-app="reputifly-agent"
    >
      <UpdatePill />

      {/* Ambient glow blobs */}
      <div className="ambient-glow ambient-glow--pink" />
      <div className="ambient-glow ambient-glow--orange" />

      <CompaniesSidebar
        open={sidebarOpen}
        companies={companies}
        activeCompanyId={activeCompanyId}
        pendingByCompany={pendingByCompany}
        sessionStatusByCompany={sessionStatusByCompany}
        onSelect={(id) => selectCompany(id)}
        onSettings={(id) => openSettings(id)}
        onDelete={(id) => deleteCompany(id)}
        onConvertToClient={(id) => convertToClient(id)}
        onClearHistory={(id) => clearChatHistory(id)}
        onCopyCurl={(id) => copyCompanyCurl(id)}
        onVisitWebsite={(id) => visitCompanyWebsite(id)}
        onRename={(id, name) => renameCompany(id, name)}
        onAddCompany={openAddCompany}
        onAddSample={() => void addSample("New sample")}
        onCloseSidebar={() => setSidebarOpen(false)}
      />

      {chatNavigationReady && <ChatPane />}
      <ChatLinkNavigation />

      {/* Topbar-anchored overflow menu (visually inside the right slot
          of .topbar; positioned absolute against .shell so it sits
          above the chat content cleanly). RAM/load chip sits immediately
          to the left of the ⋯ — purely informational, no click. */}
      <div className="shell__overflow" ref={overflowRef}>
        <RamChip />
        <button
          type="button"
          className="shell__overflowBtn"
          onClick={() => setOverflowOpen((o) => !o)}
          aria-label="More options"
          title="More"
        >
          <MoreHorizontal width={18} height={18} />
        </button>
        {overflowOpen && (
          <div className="shell__overflowMenu" role="menu">
            {/* Phones drop the chat name from the header; it heads this menu instead. */}
            {isPhone && activeCompany && <div className="shell__overflowTitle">{activeCompany.name}</div>}
            {activeCompanyId && companies.some((company) => company.id === activeCompanyId) && (
              <button type="button" role="menuitem" className="shell__overflowItem" onClick={async () => {
                setOverflowOpen(false);
                const copied = await copyTextToClipboard(chatLinkUrl(activeCompanyId));
                if (useSession.getState().activeCompanyId !== activeCompanyId) return;
                useSession.getState().setToast(copied ? "Chat link copied. Sign-in is still required." : "Couldn't copy the chat link. Please try again.", copied ? "success" : "error");
              }}>
                <Link width={14} height={14} /><span>Copy chat link</span>
              </button>
            )}
            {activeCompany && (
              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={autoOn}
                className={`shell__overflowItem shell__overflowItem--toggle ${autoOn ? "is-on" : ""}`}
                onClick={() => {
                  setOverflowOpen(false);
                  void toggleAutoApprove(activeCompany.id);
                }}
                title={
                  autoFrozen
                    ? `Auto-approve is saved ${autoOn ? "ON" : "OFF"}, but this chat is safety-paused. Changing this preference does not lift that hold. Click to turn ${autoOn ? "OFF" : "ON"}.`
                    : autoOn
                    ? "Auto-approve ON — actionable in-scope portal requests run automatically. Quotations still need your decision. Explicit schedules are separate. Click to turn OFF."
                    : "Auto-approve OFF — portal requests need manual approval. Turn ON for in-scope work; quotations still need your decision. Explicit schedules are separate."
                }
              >
                <Zap width={14} height={14} />
                <span>Auto-approve</span>
                <span className={`shell__togglePill ${autoOn ? (autoFrozen ? "is-held" : "is-on") : ""}`}>
                  {autoOn ? (autoFrozen ? "On · Held" : "On") : "Off"}
                </span>
              </button>
            )}
            {activeCompany && autoFrozen && (
              <button type="button" role="menuitem" className="shell__overflowItem" onClick={() => {
                setOverflowOpen(false);
                openSettings(activeCompany.id);
              }}>
                <AlertTriangle width={14} height={14} /><span>Review safety hold</span>
              </button>
            )}
            {activeCompany && activeCompany.kind !== "sample" && (
              <div
                className="shell__overflowEffort"
                role="group"
                aria-label="Auto-approve thinking level"
                title="Thinking level THIS client's auto-approved revisions run at (overrides the global auto-approval default). Your own messages use the composer chip."
              >
                <span className="shell__overflowEffortLabel">Auto-approve thinking</span>
                <div className="shell__overflowEffortOpts">
                  {/* "low" is never offered; it appears (active) only when it is the stored value. */}
                  {(activeCompany.autoRevisionEffort === "low"
                    ? (["low", "medium", "high", "xhigh", "max"] as const)
                    : (["medium", "high", "xhigh", "max"] as const)
                  ).map((lvl) => (
                    <button
                      key={lvl}
                      type="button"
                      className={`shell__overflowEffortOpt ${(activeCompany.autoRevisionEffort ?? "xhigh") === lvl ? "is-active" : ""}`}
                      onClick={() => setAutoRevisionEffort(activeCompany.id, lvl)}
                    >
                      {THINKING_LABEL[lvl]}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {sessionRunning && (
              <button
                type="button"
                role="menuitem"
                className="shell__overflowItem"
                onClick={() => {
                  setOverflowOpen(false);
                  void stopSession();
                }}
                title="Kill the current claude process. Your next message auto-spawns a fresh session for this client — chat history is preserved."
              >
                <Power width={14} height={14} />
                <span>Restart session</span>
              </button>
            )}
            {activeCompany && activeCompany.kind !== "sample" && <ExchangeMenuSettings key={activeCompany.id} companyId={activeCompany.id} />}
            {activeCompanyId && <button type="button" role="menuitem" className="shell__overflowItem" onClick={() => {
              setOverflowOpen(false); useReviewSheet.getState().open("all");
            }}><Layers width={14} height={14} /><span>Review Other Chats</span></button>}
            <button
              type="button"
              role="menuitem"
              className="shell__overflowItem"
              onClick={() => {
                setOverflowOpen(false);
                openScheduled();
              }}
              title="What the agent will say to a chat later, on its own — reminders you armed and check-backs it armed itself"
            >
              <Clock width={14} height={14} />
              <span>Scheduled Tasks</span>
            </button>
            <button
              type="button"
              role="menuitem"
              className="shell__overflowItem"
              onClick={() => {
                setOverflowOpen(false);
                openGlobalSettings();
              }}
              title="Global settings — e.g. the default thinking level for new samples"
            >
              <Settings width={14} height={14} />
              <span>Global settings</span>
            </button>
            <button
              type="button"
              role="menuitem"
              className="shell__overflowItem"
              onClick={() => {
                setOverflowOpen(false);
                openGuide();
              }}
            >
              <HelpCircle width={14} height={14} />
              <span>Guide</span>
            </button>
            <button
              type="button"
              role="menuitem"
              className="shell__overflowItem shell__overflowItem--danger"
              onClick={() => {
                setOverflowOpen(false);
                signOut();
              }}
            >
              <LogOut width={14} height={14} />
              <span>Sign out</span>
            </button>

            {/* Which build am I actually running? Three separate times a shipped
                fix was reported "missing" when the phone was simply on an older
                bundle, and there was no way to tell from the device -- a PWA has
                no address bar and no console. Tap to copy the id straight into a
                message. */}
            <button
              type="button"
              className="shell__overflowItem shell__buildStamp"
              onClick={() => {
                void copyTextToClipboard(runningBuildId());
                setOverflowOpen(false);
              }}
              title="Tap to copy this build's id"
            >
              <span>Build</span>
              <span className="shell__buildStampId">{runningBuildId()}</span>
            </button>
          </div>
        )}
      </div>

      {/* Modals + Guide */}
      {addCompanyModalOpen && <AddCompanyModal />}
      {settingsCompanyId && <SessionSettingsModal key={settingsCompanyId} companyId={settingsCompanyId} />}
      {guideOpen && <Guide />}
      {globalSettingsOpen && <GlobalSettingsModal />}
      {scheduledOpen && <ScheduledTasks />}

      {/* Image lightbox — fires from UserBubble + AssistantBubble + Composer
          attachment chips. When the clicked message held several images they
          open as a gallery: ‹ › buttons + ←/→ keys page through it. */}
      {lightboxItem?.blobUrl && (
        <Lightbox
          src={lightboxItem.blobUrl}
          filename={lightboxItem.filename}
          mimeType={lightboxItem.mimeType}
          onClose={() => setLightbox(null)}
          onStep={stepLightbox}
          index={lightboxIndex}
          total={lightboxItems.length}
        />
      )}

      {/* Toast: Build's snackbar (julian-ui) — a solid light card at the bottom, one at a time. */}
      {toast && (toast.action || !toastGoesToIsland(toast, isPhone, !!islandChat && !sidebarOpen)) && (
        <div ref={toastRef} className={`appToast appToast--${toast.kind}${toast.action ? " has-action" : ""}`} key={toast.id}
          role={toast.kind === "error" ? "alert" : "status"} aria-live={toast.kind === "error" ? "assertive" : "polite"}>
          <span className="appToast__text">{toast.text}</span>
          {toast.action && (
            <button type="button" className="appToast__action" onClick={() => {
              const run = toast.action?.run;
              clearToast();
              run?.();
            }}>{toast.action.label}</button>
          )}
        </div>
      )}
    </div>
  );
}
